import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTheme } from '../../contexts/ThemeContext';
import { adminAPI } from '../../services/api';
import {
  FaHome,
  FaCompass,
  FaHeart,
  FaEnvelope,
  FaBell,
  FaUser,
  FaCog,
  FaChartLine,
  FaShieldAlt,
  FaUsers,
  FaFileAlt,
  FaCreditCard,
  FaCertificate,
  FaDatabase,
  FaMemory,
  FaServer,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
  FaMoon,
  FaSun,
  FaGem,
  FaCamera
} from 'react-icons/fa';


import './Sidebar.css';

const Sidebar = ({ closeSidebar, collapsed = false, sidebarOpen = false }) => {
  const location = useLocation();
  const { user, loading } = useSelector((state) => state.auth);
  const { theme, toggleTheme } = useTheme();

  const loverMenuItems = [
    { path: '/', icon: FaHome, label: 'Accueil' },
    { path: '/stories', icon: FaCamera, label: 'Stories' },
    { path: '/discover', icon: FaCompass, label: 'Découvrir' },
    { path: '/loves', icon: FaHeart, label: 'Mes Loves' },
    { path: '/messages', icon: FaEnvelope, label: 'Messages' },
    { path: '/notifications', icon: FaBell, label: 'Notifications' },
    { path: '/wallet', icon: FaGem, label: 'Mon Portefeuille' },
    { path: `/profile/${user?.id}`, icon: FaUser, label: 'Mon Profil' },
  ];



  const adminMenuItems = [
    { path: '/admin/dashboard', icon: FaChartLine, label: 'Analyses' },
    { path: '/admin/discussions', icon: FaEnvelope, label: 'Discussions' },
    { path: '/admin/notifications', icon: FaBell, label: 'Notifications' },
    { path: '/admin/users', icon: FaUsers, label: 'Utilisateurs' },
    { path: '/admin/content', icon: FaFileAlt, label: 'Contenus' },
    { path: '/admin/certifications', icon: FaCertificate, label: 'Certifications' },
    { path: '/admin/payments', icon: FaCreditCard, label: 'Paiements' },
  ];


  const superAdminMenuItems = [
    { path: '/superadmin/logs', icon: FaFileAlt, label: 'Logs Système' },
    { path: '/superadmin/cache', icon: FaMemory, label: 'Cache & Mémoire' },
    { path: '/superadmin/database', icon: FaDatabase, label: 'Base de Données' },
    { path: '/superadmin/security', icon: FaShieldAlt, label: 'Sécurité' },
    { path: '/superadmin/config', icon: FaCog, label: 'Configuration' },
    { path: '/superadmin/servers', icon: FaServer, label: 'Serveurs' },
  ];

  const isActive = (path) => location.pathname === path;

  // Real admin stats from API
  const [adminStats, setAdminStats] = useState({
    pendingReports: 0,
    newUsers: 0,
    systemAlerts: 0
  });

  // Fetch admin stats when component mounts and user is admin/superadmin
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'superadmin') {
      const fetchAdminStats = async () => {
        try {
          const response = await adminAPI.getStats();
          setAdminStats({
            pendingReports: response.data.pendingReports || 0,
            newUsers: response.data.newSignups || 0,
            systemAlerts: response.data.systemAlerts || 0
          });
        } catch (error) {
          console.error('Error fetching admin stats:', error);
          // Keep default values (0) on error
        }
      };

      fetchAdminStats();
    }
  }, [user?.role]);

  // Mock data for superadmin system status
  const systemStatus = {
    server: 'online',
    database: 'healthy',
    cache: 'warning',
    security: 'active'
  };

  return (
    <div className={`chatoo-sidebar-wrapper ${sidebarOpen ? 'sidebar-open' : ''} ${collapsed ? 'sidebar-collapsed' : ''} ${(user?.role === 'admin' || user?.role === 'superadmin') && theme === 'dark' ? 'admin-black-theme' : ''}`}>
      {/* Admin/SuperAdmin Header */}
      {(user?.role === 'admin' || user?.role === 'superadmin') && (
        <div className="sidebar-admin-header">
          <div className="admin-role-badge">
            <FaShieldAlt className="role-icon" />
            <span className="role-text">
              {user?.role === 'admin' ? 'Administrateur' : 'Super Administrateur'}
            </span>
          </div>
          <div className="admin-status">
            <FaCheckCircle className="status-icon text-success" />
            <small className="status-text">En ligne</small>
          </div>
        </div>
      )}

      {/* User Profile Card - Only for lovers */}
      {user?.role === 'lover' && (
        <div className="sidebar-profile-card">
          <div className="profile-avatar">
            {user?.profile?.profile_picture ? (
              <img
                src={user.profile.profile_picture}
                alt={user.full_name}
                className="avatar-img"
              />
            ) : (
              <div className="avatar-placeholder">
                <FaUser />
              </div>
            )}
            {user?.is_certified && (
              <div className="certified-badge" title="Compte certifié">
                ✓
              </div>
            )}
          </div>
          <h3 className="profile-name">{user?.full_name}</h3>
          <p className="profile-stats">
            <span>{user?.profile?.total_loves || 0} Loves</span>
            <span className="separator">•</span>
            <span>{user?.profile?.total_loveshots || 0} LoveShots</span>
          </p>
        </div>
      )}

      {/* Admin Quick Stats */}
      {(user?.role === 'admin' || user?.role === 'superadmin') && (
        <div className="sidebar-admin-stats">
          <div className="stats-row">
            <div className="stat-item">
              <FaExclamationTriangle className="stat-icon text-danger" />
              <div className="stat-content">
                <span className="stat-value">{adminStats.pendingReports}</span>
                <small className="stat-label">Signalements</small>
              </div>
            </div>
            <div className="stat-item">
              <FaUsers className="stat-icon text-info" />
              <div className="stat-content">
                <span className="stat-value">+{adminStats.newUsers}</span>
                <small className="stat-label">Nouveaux</small>
              </div>
            </div>
          </div>
          {adminStats.systemAlerts > 0 && (
            <div className="alert-badge">
              <FaExclamationTriangle className="alert-icon" />
              <span>{adminStats.systemAlerts} alerte(s) système</span>
            </div>
          )}
        </div>
      )}

      {/* SuperAdmin System Status */}
      {user?.role === 'superadmin' && (
        <div className="sidebar-system-status">
          <h6 className="status-title">État Système</h6>
          <div className="status-indicators">
            <div className="status-item">
              <FaServer className={`status-dot ${systemStatus.server === 'online' ? 'text-success' : 'text-danger'}`} />
              <small>Serveur</small>
            </div>
            <div className="status-item">
              <FaDatabase className={`status-dot ${systemStatus.database === 'healthy' ? 'text-success' : 'text-warning'}`} />
              <small>Base</small>
            </div>
            <div className="status-item">
              <FaMemory className={`status-dot ${systemStatus.cache === 'warning' ? 'text-warning' : 'text-success'}`} />
              <small>Cache</small>
            </div>
            <div className="status-item">
              <FaShieldAlt className={`status-dot ${systemStatus.security === 'active' ? 'text-success' : 'text-danger'}`} />
              <small>Sécurité</small>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Menu */}
      <nav className="sidebar-nav">
        {loading ? (
          <div className="sidebar-loading">
            <FaClock className="loading-icon" />
            <span>Chargement...</span>
          </div>
        ) : (
          <>
            {user?.role === 'lover' && (
              <>
                <div className="sidebar-section-title">Navigation</div>
                {loverMenuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-nav-item ${isActive(item.path) ? 'active' : ''}`}
                    onClick={closeSidebar}
                  >
                    <item.icon className="nav-item-icon" />
                    <span className="nav-item-label">{item.label}</span>
                  </Link>
                ))}
              </>
            )}

            {(user?.role === 'admin' || user?.role === 'superadmin') && (
              <>
                <div className="sidebar-section-title">Administration</div>
                {adminMenuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-nav-item ${isActive(item.path) ? 'active' : ''}`}
                    onClick={closeSidebar}
                  >
                    <item.icon className="nav-item-icon" />
                    <span className="nav-item-label">{item.label}</span>
                  </Link>
                ))}

                {user?.role === 'superadmin' && (
                  <>
                    <div className="sidebar-section-title">Système</div>
                    {superAdminMenuItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`sidebar-nav-item ${isActive(item.path) ? 'active' : ''}`}
                        onClick={closeSidebar}
                      >
                        <item.icon className="nav-item-icon" />
                        <span className="nav-item-label">{item.label}</span>
                      </Link>
                    ))}
                  </>
                )}
              </>
            )}
          </>
        )}
      </nav>

      {/* Settings */}
      <div className="sidebar-divider"></div>
      <Link to="/settings" className="sidebar-nav-item" onClick={closeSidebar}>
        <FaCog className="nav-item-icon" />
        <span className="nav-item-label">Paramètres</span>
      </Link>

      {/* Theme Toggle */}
      <div className="sidebar-nav-item theme-toggle-item" onClick={toggleTheme} title={theme === 'light' ? 'Passer au thème sombre' : 'Passer au thème clair'}>
        {theme === 'light' ? <FaMoon className="nav-item-icon" /> : <FaSun className="nav-item-icon" />}
        <span className="nav-item-label">{theme === 'light' ? 'Thème Sombre' : 'Thème Clair'}</span>
      </div>

      {/* Footer */}
      <div className="sidebar-footer">
        <p className="footer-text">© 2024 CHATOO</p>
        <div className="footer-links">
          <a href="/about">À propos</a>
          <span>•</span>
          <a href="/privacy">Confidentialité</a>
          <span>•</span>
          <a href="/terms">Conditions</a>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
